import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface RowActionItem {
  /** Label rendered in the menu (and aria-label / title when shown inline). */
  label: string;
  /** Lucide icon for the menu row. Sized to 14px to match cell line-height. */
  icon: LucideIcon;
  /** Synchronous click handler. */
  onClick?: () => void;
  /** Optional disabled state; the row still renders, just dimmed + unclickable. */
  disabled?: boolean;
  /** Tints the menu row destructive (red). Use sparingly. */
  destructive?: boolean;
  /** Optional inline node override (e.g. a ConfirmDialog trigger). When set,
   *  the menu renders this node inside a DropdownMenuItem with `asChild`. */
  render?: React.ReactNode;
}

interface RowActionsProps {
  /** Items shown in the overflow menu in order. Use `null` / `undefined` to skip. */
  items: ReadonlyArray<RowActionItem | null | undefined | false>;
  /** Optional aria-label override for the trigger. Defaults to "Row actions". */
  triggerLabel?: string;
  /**
   * Visible trigger text (e.g. "More"). Renders an outline button with the
   * text instead of the icon-only kebab, for headers with room to spare.
   */
  triggerText?: string;
}

/**
 * Three-dot overflow menu for table row actions. Pair with one inline primary
 * action (Resolve / Revert / etc.) — this collapses the 4-5 secondary actions
 * that would otherwise cram a row. Keeps text labels visible inside the
 * menu so actions stay guessable.
 *
 * The cluster naturally inherits `td.actions` styling on mobile (the
 * card-stack pattern puts the cluster on its own full-width row).
 */
export function RowActions({ items, triggerLabel = 'Row actions', triggerText }: RowActionsProps): JSX.Element {
  // Destructive items sink to the bottom, behind a separator, so a slip of
  // the pointer from the everyday actions can't land on one.
  const all = items.filter((i): i is RowActionItem => Boolean(i));
  const safe = all.filter((i) => !i.destructive);
  const destructive = all.filter((i) => i.destructive);
  const visible = [...safe, ...destructive];

  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        {triggerText ? (
          <Button size="sm" variant="outline" aria-label={triggerLabel} title={triggerLabel}>
            <MoreHorizontal className="mr-1.5 h-3.5 w-3.5" />
            {triggerText}
          </Button>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            aria-label={triggerLabel}
            title={triggerLabel}
            className="h-8 w-8"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={4}
          className={cn(
            'z-50 min-w-[10rem] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-overlay',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          )}
        >
          {visible.map((item, idx) => {
            const Icon = item.icon;
            const separator =
              idx === safe.length && safe.length > 0 && destructive.length > 0 ? (
                <DropdownMenuPrimitive.Separator className="-mx-1 my-1 h-px bg-border" />
              ) : null;
            const itemClass = cn(
              'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
              'focus:bg-accent focus:text-accent-foreground',
              'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              item.destructive && 'text-destructive focus:text-destructive',
            );

            if (item.render) {
              return (
                <React.Fragment key={`${item.label}-${idx}`}>
                  {separator}
                  <DropdownMenuPrimitive.Item
                    asChild
                    disabled={item.disabled}
                    onSelect={(e) => e.preventDefault()}
                    className={itemClass}
                  >
                    {item.render}
                  </DropdownMenuPrimitive.Item>
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={`${item.label}-${idx}`}>
                {separator}
                <DropdownMenuPrimitive.Item
                  disabled={item.disabled}
                  onSelect={() => item.onClick?.()}
                  className={itemClass}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {item.label}
                </DropdownMenuPrimitive.Item>
              </React.Fragment>
            );
          })}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
